import { useGetVesselCatalogMutation } from 'app/api/facade';
import { UserI } from 'app/utils/userSlice';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OrderParams, TargetCatalogFilter, TargetCatalogSearchRequest } from 'ais-facade-api/api';
import { Table, Pagination } from 'app/components';
import { columns as tableColumns } from './columns';
import { Button, AtonsFilterPanel, Input, PageHeader, Typo, Icon } from 'app/components';

import { useDebounce, useLocalStorage, useOnClickOutside } from 'app/hooks';
import { throttle } from 'app/helpers';
import { getI18n, useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { setAtonsCatalogFilter } from 'app/utils/facadeReducer';

import styles from './AtonsPage.module.scss';

interface Props {
  user: UserI | null;
}

const initialBody: TargetCatalogSearchRequest = {
  filter: {
    fleetOnly: false,
    source: [],
    type: [],
    country: [],
    status: [],
    specialType: ['ATON'],
  },
  order: {
    direction: 'DESC',
    by: 'LUT',
  },
  pagination: {
    offset: 0,
    limit: 20,
  },
};

export interface SortingInterface {
  direction: 'ASC' | 'DESC' | 'default';
  by: string;
}

export interface Filters extends Omit<TargetCatalogFilter, 'bbox'> {
  touched?: boolean;
  statusAll: boolean;
  typeAll: boolean;
  countryAll: boolean;
  sourceAll: boolean;
  fleetGroupAll: boolean;
}

export const AtonsPage = ({ user }: Props) => {
  const { t } = useTranslation();
  const language = useMemo(() => getI18n().language, [t]);
  const dispatch = useAppDispatch();

  const [getCatalog, { data: catalog, ...catalogRequest }] = useGetVesselCatalogMutation({
    fixedCacheKey: 'get-vessels-mutation',
  });

  const debouncedGetCatalogHandler = useCallback(throttle(getCatalog, 500), []);

  const filters = useAppSelector((state) => state.facadeSlice.atonsCatalogFilter);

  const [currentPage, setCurrentPage] = useState(1);
  const currentOffset = useMemo(() => (currentPage - 1) * 20, [currentPage]);

  const [showFilter, setShowFilter] = useState<boolean>(false);

  const [localFilter, setLocalFilter] = useLocalStorage<Filters | null>('filters-atonsCatalog', null);
  const filterPanelRef = useRef<HTMLDivElement>(null);

  const initialSort: OrderParams = {
    direction: 'DESC',
    by: 'LUT',
  };

  const [sorting, setSorting] = useState<SortingInterface>(initialSort);

  const [term, setTerm] = useLocalStorage<string>('atonsCatalogTerm', '');
  const debouncedInputValue = useDebounce<string>(term, 500);

  const columns = useMemo(() => tableColumns, []);

  useEffect(() => {
    catalogRequest.reset();
  }, []);

  useEffect(() => {
    if (localFilter !== null) {
      dispatch(setAtonsCatalogFilter(localFilter));
    }
  }, [localFilter]);

  useEffect(() => {
    setCurrentPage(1);

    debouncedGetCatalogHandler({
      ...initialBody,
      filter: {
        touched: filters?.touched || false,
        fleetOnly: filters?.fleetOnly || false,
        term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
        source: filters?.sourceAll ? [] : filters?.source,
        type: filters?.typeAll ? [] : filters?.type,
        country: filters?.countryAll ? [] : filters?.country,
        status: filters?.statusAll ? [] : filters?.status,
        specialType: ['ATON'],
      },
      pagination: {
        offset: 0,
        limit: 20,
      },
      order:
        sorting.direction === 'default'
          ? initialSort
          : {
            direction: sorting.direction,
            by: sorting.by,
          },
    });
  }, [debouncedInputValue, language]);

  useEffect(() => {
    if (user) {
      debouncedGetCatalogHandler({
        ...initialBody,
        filter: {
          fleetOnly: filters?.fleetOnly || false,
          term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
          source: filters?.sourceAll ? [] : filters?.source,
          type: filters?.typeAll ? [] : filters?.type,
          country: filters?.countryAll ? [] : filters?.country,
          status: filters?.statusAll ? [] : filters?.status,
          specialType: ['ATON'],
        },
        pagination: {
          offset: currentOffset,
          limit: 20,
        },
        order:
          sorting.direction === 'default'
            ? initialSort
            : {
              direction: sorting.direction,
              by: sorting.by,
            },
      });
    }
  }, [currentOffset, user]);

  useEffect(() => {
    if (user) {
      setCurrentPage(1);

      debouncedGetCatalogHandler({
        ...initialBody,
        filter: {
          fleetOnly: filters?.fleetOnly || false,
          term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
          source: filters?.sourceAll ? [] : filters?.source,
          type: filters?.typeAll ? [] : filters?.type,
          country: filters?.countryAll ? [] : filters?.country,
          status: filters?.statusAll ? [] : filters?.status,
          specialType: ['ATON'],
        },
        order:
          sorting.direction === 'default'
            ? initialSort
            : {
              direction: sorting.direction,
              by: sorting.by,
            },
      });
    }
  }, [sorting]);

  useEffect(() => {
    if (user) {
      setCurrentPage(1);
      setSorting(initialBody.order);

      debouncedGetCatalogHandler({
        ...initialBody,

        filter: {
          fleetOnly: filters?.fleetOnly || false,
          term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
          source: filters?.sourceAll ? [] : filters?.source,
          type: filters?.typeAll ? [] : filters?.type,
          country: filters?.countryAll ? [] : filters?.country,
          status: filters?.statusAll ? [] : filters?.status,
          specialType: ['ATON'],
        },
      });
    }
  }, [filters]);

  const clickOutsideHandler = () => {
    setShowFilter(false);
  };
  useOnClickOutside(filterPanelRef, clickOutsideHandler);

  return (
    <div className={styles.page}>
      <PageHeader iconName="catalog-atons" title={t('AtonsPageHeader')} />
      <div className={styles.table_actions}>
        <Typo
          variant="span"
          size="m"
          value={`${t('CatalogAtonsTotal')}${filters?.touched ? ` ${t('AppliedFilters')}:` : ':'} ${catalog ? catalog.total : '–'
            }`}
        />
        <Button variant="general-small" onClick={() => setShowFilter(true)} className={styles.icon_button}>
          {filters?.touched ? <Icon name="filter-active" size="14px" /> : <Icon name="filter" size="14px" />}

          <Typo value={t('Filter')} variant="span" />
        </Button>
        <Input
          className={styles.table_search}
          type="search"
          onChange={(e) => {
            setTerm(e.target.value);
            localStorage.setItem('atonsCatalogTerm', JSON.stringify(debouncedInputValue));
          }}
          placeholder={t('SearchPlaceholder')}
          value={term}
        />
      </div>

      {catalogRequest.isLoading && <span className={styles.loading}>{t('SearchingText')}</span>}

      {!catalogRequest.isLoading && catalog?.results?.length === 0 && <p className={styles.no_data}>{t('NoResultsFound')}</p>}

      {catalog?.results && catalog?.total && catalog.results.length > 0 ? (
        <>
          <div className={styles.table_container}>
            <Table
              manualSort={true}
              sorting={sorting}
              setSorting={setSorting}
              columns={columns}
              data={catalog.results}
              total={catalog.total}
            />
          </div>
          <div className={styles.pagination_container}>
            <Pagination
              totalCount={catalog.total}
              currentPage={currentPage}
              pageChangeHandler={setCurrentPage}
              rowsPerPage={20}
            />
          </div>
        </>
      ) : null}
      {showFilter && (
        <AtonsFilterPanel
          innerRef={filterPanelRef}
          onClose={() => {
            setShowFilter(false);
          }}
          filterStateControl={[localFilter, setLocalFilter]}
        />
      )}
    </div>
  );
};
